/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */
/**
 * URL state helpers — URL is the source of truth for routing.
 */

export function getQuery(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

export function normalizeLoggerId(input: string): string {
  const trimmed = (input || "").trim();
  return trimmed.replace(/^RK-/i, "");
}

export function setQueryParam(key: string, value: string | null): void {
  const url = new URL(window.location.href);
  if (value == null || value === "") {
    url.searchParams.delete(key);
  } else {
    url.searchParams.set(key, value);
  }
  window.history.replaceState({}, "", url.toString());
}

export function parseLoggersCsv(raw: string | null): string[] | null {
  if (!raw) return null;
  const parts = raw
    .split(",")
    .map((p) => normalizeLoggerId(p))
    .filter(Boolean);
  return parts.length ? parts : null;
}

/**
 * Telemetry source modes.
 *  - "hlsMetadata"   — read EXT-X-DATERANGE tags from the HLS playlist.
 *                       Default. Synchronised with what the viewer sees on
 *                       screen (~2 s HLS-segment cadence). Best for replay
 *                       and broadcast viewing.
 *  - "liveWebSocket" — push channel fed by KDS at producer rate
 *                       (engineer / pit-crew live mode). Decoupled from HLS
 *                       segment cadence; data arrives the moment the D700
 *                       publishes it, several seconds ahead of the video.
 *  - "hlsInbandId3"  — telemetry carried IN-BAND as ID3 timed metadata muxed
 *                       inside the TS segments (not in the playlist). hls.js
 *                       surfaces it via FRAG_PARSING_METADATA with each
 *                       sample's PTS, so it is frame-locked to the video by
 *                       the decoder — correct for every viewer at any latency,
 *                       with no playlist-window or PDT-correlation dependency.
 *                       This is the successor to "hlsMetadata".
 */
export type TelemetrySource = "hlsMetadata" | "liveWebSocket" | "hlsInbandId3";

export function isTelemetrySource(v: unknown): v is TelemetrySource {
  return (
    v === "hlsMetadata" || v === "liveWebSocket" || v === "hlsInbandId3"
  );
}
